(() => {
  'use strict';

  const theme = window.nexoTheme || {};
  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  let lastFocused = null;
  let toastTimer = null;

  const money = (cents) => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(theme.locale || 'es-CO', {
        style: 'currency',
        currency: theme.currency || 'COP',
        maximumFractionDigits: (theme.currency || 'COP') === 'COP' ? 0 : 2
      }).format(amount);
    } catch (error) {
      return amount.toFixed(2);
    }
  };

  const toast = (message) => {
    const element = qs('[data-toast]');
    if (!element) return;
    window.clearTimeout(toastTimer);
    element.textContent = message;
    element.classList.add('is-visible');
    toastTimer = window.setTimeout(() => element.classList.remove('is-visible'), 2800);
  };

  const setDrawer = (open) => {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    document.body.classList.toggle('drawer-open', open);
    if (open) {
      lastFocused = document.activeElement;
      window.setTimeout(() => qs('.drawer__panel', drawer)?.focus(), 50);
    } else if (lastFocused) {
      lastFocused.focus?.();
    }
  };

  const setMenu = (open) => {
    const menu = qs('[data-mobile-menu]');
    const trigger = qs('[data-menu-open]');
    if (!menu) return;
    menu.classList.toggle('is-open', open);
    menu.setAttribute('aria-hidden', String(!open));
    trigger?.setAttribute('aria-expanded', String(open));
    document.body.classList.toggle('menu-open', open);
  };

  const refreshCart = async (open = false) => {
    const response = await fetch(`${theme.routes.cart}?section_id=cart-drawer`, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    });
    if (!response.ok) throw new Error('Cart render failed');
    const html = await response.text();
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    const nextDrawer = qs('[data-cart-drawer-section]', wrapper);
    const currentDrawer = qs('[data-cart-drawer-section]');
    if (nextDrawer && currentDrawer) currentDrawer.replaceWith(nextDrawer);

    const cartResponse = await fetch(`${theme.routes.cart}.js`, { credentials: 'same-origin' });
    const cart = await cartResponse.json();
    qsa('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.hidden = cart.item_count === 0;
    });
    if (open) setDrawer(true);
    document.dispatchEvent(new CustomEvent('nexo:cart-updated', { detail: cart }));
    return cart;
  };

  const changeCartLine = async (key, quantity, button) => {
    button?.setAttribute('disabled', 'disabled');
    try {
      const response = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, Number(quantity)) })
      });
      if (!response.ok) throw new Error('Cart update failed');
      await refreshCart(true);
    } catch (error) {
      toast(theme.strings.error || 'Error');
      button?.removeAttribute('disabled');
    }
  };

  const submitProductForm = async (form, submitter) => {
    const button = submitter || qs('[type="submit"]', form);
    button?.classList.add('is-loading');
    button?.setAttribute('aria-busy', 'true');
    try {
      const formData = new FormData(form);
      const response = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        body: formData
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || theme.strings.error);
      await refreshCart(true);
      toast(theme.strings.added || 'Agregado');
    } catch (error) {
      toast(error.message || theme.strings.error || 'Error');
    } finally {
      button?.classList.remove('is-loading');
      button?.removeAttribute('aria-busy');
    }
  };

  const quickAdd = async (variantId, button) => {
    button?.setAttribute('disabled', 'disabled');
    try {
      const response = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });
      if (!response.ok) throw new Error(theme.strings.error);
      await refreshCart(true);
      toast(theme.strings.added || 'Agregado');
    } catch (error) {
      toast(error.message || theme.strings.error || 'Error');
      button?.removeAttribute('disabled');
    }
  };

  const parseProduct = (section) => {
    const script = qs('[data-product-json]', section);
    if (!script) return null;
    try { return JSON.parse(script.textContent); } catch (error) { return null; }
  };

  const selectedOptions = (section) => qsa('[data-option-index]', section).map((group) => {
    const selectedButton = qs('[data-option-value].is-selected', group);
    const select = qs('select', group);
    return selectedButton?.dataset.optionValue || select?.value || '';
  });

  const findVariant = (product, options) => product?.variants?.find((variant) =>
    variant.options.every((option, index) => option === options[index])
  );

  const updateOfferPrices = (section, variant) => {
    qsa('[data-offer]', section).forEach((offer) => {
      const quantity = Number(offer.dataset.quantity || 1);
      const discount = Math.min(100, Math.max(0, Number(offer.dataset.discount || 0)));
      const total = Math.round(variant.price * quantity * (1 - discount / 100));
      const totalNode = qs('[data-offer-total]', offer);
      const unitNode = qs('[data-offer-unit]', offer);
      if (totalNode) totalNode.textContent = money(total);
      if (unitNode) unitNode.textContent = `${money(Math.round(total / quantity))} c/u`;
    });
  };

  const updateProductVariant = (section, variant) => {
    const idInput = qs('input[name="id"]', section);
    const submit = qs('[data-product-submit]', section);
    const price = qs('[data-product-price]', section);
    const compare = qs('[data-product-compare]', section);
    const save = qs('[data-product-save]', section);

    if (!variant) {
      if (submit) {
        submit.disabled = true;
        qs('.button__label', submit).textContent = theme.strings.unavailable;
      }
      return;
    }

    if (idInput) idInput.value = variant.id;
    if (price) price.textContent = money(variant.price);
    if (compare) {
      compare.textContent = money(variant.compare_at_price);
      compare.hidden = !(variant.compare_at_price > variant.price);
    }
    if (save) {
      const saving = Number(variant.compare_at_price || 0) - Number(variant.price || 0);
      const savingPercentage = saving > 0 && Number(variant.compare_at_price) > 0
        ? Math.round((saving / Number(variant.compare_at_price)) * 100)
        : 0;
      save.textContent = savingPercentage > 0 ? `AHORRA ${savingPercentage}%` : '';
      save.hidden = saving <= 0;
    }
    if (submit) {
      submit.disabled = !variant.available;
      qs('.button__label', submit).textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
    }
    qsa('[data-sticky-price]', section).forEach((node) => { node.textContent = money(variant.price); });
    qsa('[data-sticky-submit]', section).forEach((node) => {
      node.disabled = !variant.available;
      node.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
    });
    updateOfferPrices(section, variant);

    const media = qs(`[data-media-id="${variant.featured_media?.id}"]`, section);
    const galleryTrack = media?.closest('.product-gallery__track');
    if (media && galleryTrack && galleryTrack.scrollWidth > galleryTrack.clientWidth) {
      galleryTrack.scrollTo({ left: media.offsetLeft - galleryTrack.offsetLeft, behavior: 'smooth' });
    }
    if (history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url.toString());
    }
  };

  const selectOffer = (offer) => {
    const section = offer.closest('[data-product-section]');
    const quantity = Number(offer.dataset.quantity || 1);
    qsa('[data-offer]', section).forEach((card) => card.classList.toggle('is-selected', card === offer));
    const radio = qs('input[type="radio"]', offer);
    if (radio) radio.checked = true;
    const input = qs('[data-quantity-input]', section);
    if (input) {
      input.value = quantity;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const offerProperty = qs('[data-offer-property]', section);
    if (offerProperty) offerProperty.value = offer.dataset.offerName || `${quantity} unidad${quantity === 1 ? '' : 'es'}`;
    const summaryQuantity = qs('[data-order-quantity]', section);
    const summaryTotal = qs('[data-order-total]', section);
    if (summaryQuantity) summaryQuantity.textContent = `${quantity} unidad${quantity === 1 ? '' : 'es'}`;
    if (summaryTotal) summaryTotal.textContent = qs('[data-offer-total]', offer)?.textContent || '';
    section.dispatchEvent(new CustomEvent('nexo:offer-change', { bubbles: true, detail: { quantity, label: offerProperty?.value || '' } }));
  };

  const setQuantity = (input, value) => {
    const min = Number(input.min || 1);
    const max = Number(input.max || 999);
    input.value = Math.min(max, Math.max(min, Number(value) || min));
    const section = input.closest('[data-product-section]');
    if (section) {
      const matching = qsa('[data-offer]', section).find((offer) => Number(offer.dataset.quantity) === Number(input.value));
      qsa('[data-offer]', section).forEach((offer) => offer.classList.toggle('is-selected', offer === matching));
    }
  };

  const addBusinessDays = (start, days) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    let remaining = Number(days || 0);
    while (remaining > 0) {
      date.setDate(date.getDate() + 1);
      const weekday = date.getDay();
      if (weekday !== 0 && weekday !== 6) remaining -= 1;
    }
    return date;
  };

  const dateLabel = (date) => new Intl.DateTimeFormat(theme.locale || 'es-CO', {
    day: 'numeric', month: 'short'
  }).format(date).replace('.', '');

  const initDeliveryDates = () => {
    qsa('[data-delivery-estimate]').forEach((card) => {
      const now = new Date();
      const dispatchDays = Number(card.dataset.dispatchDays || 1);
      const minDays = Number(card.dataset.minDays || 3);
      const maxDays = Number(card.dataset.maxDays || 6);
      const todayNode = qs('[data-date-today]', card);
      const dispatchNode = qs('[data-date-dispatch]', card);
      const deliveryNode = qs('[data-date-delivery]', card);
      if (todayNode) todayNode.textContent = dateLabel(now);
      if (dispatchNode) dispatchNode.textContent = dateLabel(addBusinessDays(now, dispatchDays));
      if (deliveryNode) deliveryNode.textContent = `${dateLabel(addBusinessDays(now, minDays))}–${dateLabel(addBusinessDays(now, maxDays))}`;
    });
  };

  const initProducts = () => {
    qsa('[data-product-section]').forEach((section) => {
      const product = parseProduct(section);
      if (!product) return;
      const variant = findVariant(product, selectedOptions(section)) || product.variants[0];
      updateOfferPrices(section, variant);
      const selectedOffer = qs('[data-offer].is-selected', section);
      const quantityInput = qs('[data-quantity-input]', section);
      if (selectedOffer && quantityInput && Number(quantityInput.value) !== 1) {
        quantityInput.value = Number(selectedOffer.dataset.quantity || 1);
      }

      const submit = qs('[data-product-submit]', section);
      const sticky = qs('[data-sticky-atc]', section);
      if (submit && sticky && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(([entry]) => {
          sticky.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        }, { threshold: 0 });
        observer.observe(submit);
      }
    });
  };

  const initProductGalleries = (scope = document) => {
    qsa('[data-product-gallery]', scope).forEach((gallery) => {
      if (gallery.dataset.initialized === 'true') return;
      const track = qs('[data-gallery-track]', gallery);
      const slides = qsa('.product-media', track);
      const dots = qs('[data-gallery-dots]', gallery);
      if (!track || slides.length < 2) return;
      gallery.dataset.initialized = 'true';
      let active = 0;
      let scrollTimer = null;
      const current = qs('[data-gallery-current]', gallery);
      const update = () => {
        qsa('[data-gallery-dot]', gallery).forEach((dot, index) => {
          dot.classList.toggle('is-active', index === active);
          dot.setAttribute('aria-current', index === active ? 'true' : 'false');
        });
        if (current) current.textContent = String(active + 1);
      };
      const goTo = (index) => {
        active = (index + slides.length) % slides.length;
        const slide = slides[active];
        track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
        update();
      };
      slides.forEach((slide, index) => {
        if (!dots) return;
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = `product-gallery__dot${index === 0 ? ' is-active' : ''}`;
        dot.dataset.galleryDot = String(index);
        dot.setAttribute('aria-label', `Ver imagen ${index + 1}`);
        dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
        dots.appendChild(dot);
      });
      qs('[data-gallery-prev]', gallery)?.addEventListener('click', () => goTo(active - 1));
      qs('[data-gallery-next]', gallery)?.addEventListener('click', () => goTo(active + 1));
      dots?.addEventListener('click', (event) => {
        const dot = event.target.closest('[data-gallery-dot]');
        if (dot) goTo(Number(dot.dataset.galleryDot));
      });
      track.addEventListener('scroll', () => {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(() => {
          const left = track.getBoundingClientRect().left;
          active = slides.reduce((best, slide, index) => Math.abs(slide.getBoundingClientRect().left - left) < Math.abs(slides[best].getBoundingClientRect().left - left) ? index : best, 0);
          update();
        }, 80);
      }, { passive: true });
    });
  };

  const initTestimonialCarousels = (scope = document) => {
    qsa('[data-testimonials-carousel]', scope).forEach((carousel) => {
      if (carousel.dataset.initialized === 'true') return;
      const track = qs('[data-testimonials-track]', carousel);
      const slides = qsa('[data-testimonial-slide]', carousel);
      if (!track || slides.length < 2) return;
      carousel.dataset.initialized = 'true';

      const dots = qs('[data-testimonials-dots]', carousel);
      let activeIndex = 0;
      let timer = null;
      let scrollTimer = null;
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const autoplay = carousel.dataset.autoplay === 'true' && !prefersReducedMotion;
      const interval = Math.max(3000, Number(carousel.dataset.interval || 5000));

      const updateDots = () => qsa('[data-testimonials-dot]', carousel).forEach((dot, index) => {
        const active = index === activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      const goTo = (index, behavior = 'smooth') => {
        activeIndex = (index + slides.length) % slides.length;
        const slide = slides[activeIndex];
        track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior });
        updateDots();
      };

      const stop = () => window.clearInterval(timer);
      const start = () => {
        stop();
        if (autoplay && !document.hidden) timer = window.setInterval(() => goTo(activeIndex + 1), interval);
      };

      if (dots) {
        slides.forEach((slide, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = `carousel-dot${index === 0 ? ' is-active' : ''}`;
          dot.dataset.testimonialsDot = String(index);
          dot.setAttribute('aria-label', `Ver reseña ${index + 1}`);
          dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
          dots.appendChild(dot);
        });
      }

      qs('[data-testimonials-prev]', carousel)?.addEventListener('click', () => { goTo(activeIndex - 1); start(); });
      qs('[data-testimonials-next]', carousel)?.addEventListener('click', () => { goTo(activeIndex + 1); start(); });
      dots?.addEventListener('click', (event) => {
        const dot = event.target.closest('[data-testimonials-dot]');
        if (dot) { goTo(Number(dot.dataset.testimonialsDot)); start(); }
      });
      track.addEventListener('scroll', () => {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(() => {
          const trackLeft = track.getBoundingClientRect().left;
          activeIndex = slides.reduce((best, slide, index) =>
            Math.abs(slide.getBoundingClientRect().left - trackLeft) < Math.abs(slides[best].getBoundingClientRect().left - trackLeft) ? index : best, 0);
          updateDots();
        }, 80);
      }, { passive: true });
      carousel.addEventListener('mouseenter', stop);
      carousel.addEventListener('mouseleave', start);
      carousel.addEventListener('focusin', stop);
      carousel.addEventListener('focusout', start);
      document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());
      start();
    });
  };

  document.addEventListener('click', (event) => {
    const cartOpen = event.target.closest('[data-cart-open]');
    const cartClose = event.target.closest('[data-cart-close]');
    const menuOpen = event.target.closest('[data-menu-open]');
    const menuClose = event.target.closest('[data-menu-close]');
    const cartChange = event.target.closest('[data-cart-change]');
    const quantityButton = event.target.closest('[data-quantity-change]');
    const optionButton = event.target.closest('[data-option-value]');
    const offer = event.target.closest('[data-offer]');
    const stickySubmit = event.target.closest('[data-sticky-submit]');
    const quickAddButton = event.target.closest('[data-quick-add]');

    if (cartOpen) setDrawer(true);
    if (cartClose) setDrawer(false);
    if (menuOpen) setMenu(true);
    if (menuClose || (event.target.matches('[data-mobile-menu]'))) setMenu(false);
    if (cartChange) changeCartLine(cartChange.dataset.key, cartChange.dataset.quantity, cartChange);
    if (quantityButton) {
      const wrapper = quantityButton.closest('[data-quantity]');
      const input = qs('[data-quantity-input]', wrapper);
      setQuantity(input, Number(input.value) + Number(quantityButton.dataset.quantityChange));
    }
    if (optionButton) {
      const group = optionButton.closest('[data-option-index]');
      qsa('[data-option-value]', group).forEach((button) => button.classList.toggle('is-selected', button === optionButton));
      const section = optionButton.closest('[data-product-section]');
      updateProductVariant(section, findVariant(parseProduct(section), selectedOptions(section)));
    }
    if (offer) selectOffer(offer);
    if (stickySubmit) {
      const section = stickySubmit.closest('[data-product-section]');
      qs('[data-product-submit]', section)?.click();
    }
    if (quickAddButton) quickAdd(quickAddButton.dataset.quickAdd, quickAddButton);
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-variant-select]')) {
      const section = event.target.closest('[data-product-section]');
      updateProductVariant(section, findVariant(parseProduct(section), selectedOptions(section)));
    }
    if (event.target.matches('[data-quantity-input]')) setQuantity(event.target, event.target.value);
    if (event.target.matches('[data-localization-select]')) event.target.form?.submit();
  });

  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-before-after-range]')) {
      event.target.closest('[data-before-after]')?.style.setProperty('--position', `${event.target.value}%`);
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form.product-form');
    if (!form) return;
    event.preventDefault();
    submitProductForm(form, event.submitter);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      setDrawer(false);
      setMenu(false);
    }
  });

  initProducts();
  initProductGalleries();
  initDeliveryDates();
  initTestimonialCarousels();

  document.addEventListener('shopify:section:load', () => {
    initProducts();
    initProductGalleries();
    initDeliveryDates();
    initTestimonialCarousels();
  });
})();
